import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { timezoneState, convertDateToTimezone } from 'global-stores/timezone';

/**
 * Hook to convert dates to user's selected timezone
 * Provides a convenient way to format dates in components
 * 
 * @returns Object with formatDate function and current timezone info
 * 
 * @example
 * const { formatDate, timezone } = useTimezoneDate();
 * 
 * // Format current date
 * const formattedNow = formatDate(new Date(), 'MM/dd/yyyy HH:mm');
 * 
 * // Format UTC date string  
 * const formattedUTC = formatDate('2024-01-15T10:30:00Z', 'MMM dd, yyyy');
 * 
 * // Format timestamp
 * const formattedTimestamp = formatDate(1704967800, 'yyyy-MM-dd');
 */
export const useTimezoneDate = () => {
	const timezone = useRecoilValue(timezoneState);

	/**
	 * Formats a date using the user's selected timezone
	 */
	const formatDate = useCallback((
		dateInput: string | number | Date,
		dateFormat = 'MM/dd/yyyy',
		hideTimeZone = false,
		utcTimeZone = false
	) => {
		return convertDateToTimezone(dateInput, dateFormat, timezone.timezone, hideTimeZone, utcTimeZone);
	}, [timezone.timezone]);

	return {
		formatDate,
		timezone: {
			name: timezone.displayName,
			value: timezone.timezone,
			abbreviation: timezone.abbreviation
		}
	};
};
